import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LegalPage from "./LegalPage";
import api from "@/lib/api";

const BRAND = "Manthan Foundation";
const EMAIL = "info@manthanfoundation.in";

const DEFAULT_SECTIONS = [
  {
    id: "overview",
    title: "1. Overview and Purpose",
    content: (
      <>
        <p><strong>{BRAND}</strong> operates an educational podcast programme as part of its Skilled Teachers initiative. This programme provides a platform for practising educators across India to share their professional experiences, teaching innovations, and leadership insights with a wider audience.</p>
        <p>By applying for or participating in the {BRAND} podcast ("the Podcast"), you ("the Participant") agree to these Podcast Participation Terms and Consent ("Podcast Terms") in their entirety.</p>
        <p>These Podcast Terms should be read alongside our <Link to="/terms">Terms &amp; Conditions</Link> and <Link to="/privacy">Privacy Policy</Link>, which collectively govern your participation.</p>
      </>
    ),
  },
  {
    id: "participation",
    title: "2. Participation",
    content: (
      <>
        <p>Participation in the {BRAND} podcast is open to eligible educators and school professionals. By submitting an application:</p>
        <ul>
          <li>You confirm that all information provided in your application is accurate and complete.</li>
          <li>You understand that submission of an application does not guarantee selection or invitation.</li>
          <li>Applications are reviewed by the {BRAND} editorial team, and selection decisions are final.</li>
          <li>You agree to be available for scheduled recording sessions at the agreed date and time.</li>
          <li>You confirm that you have obtained any required permission from your employing institution before applying, or will do so before participation.</li>
        </ul>
      </>
    ),
  },
  {
    id: "recording-consent",
    title: "3. Recording Consent",
    content: (
      <>
        <p>By confirming your participation in the podcast, you grant {BRAND} your full and irrevocable consent to:</p>
        <ul>
          <li><strong>Audio Recording:</strong> Record your voice, speech, and verbal contributions during the podcast session.</li>
          <li><strong>Video Recording:</strong> Record your visual likeness, image, and appearance during any video-enabled session.</li>
          <li><strong>Photography:</strong> Capture still photographs of you for use in connection with the podcast or promotional materials.</li>
          <li><strong>Screen Recording:</strong> Record any screen shares, presentations, or digital materials you display during the session.</li>
        </ul>
        <p>You acknowledge that {BRAND} will retain full ownership of all recorded content.</p>
      </>
    ),
  },
  {
    id: "editing-rights",
    title: "4. Editing Rights",
    content: (
      <>
        <p>You grant {BRAND} the right to:</p>
        <ul>
          <li>Edit, cut, compress, or rearrange the recorded content for clarity, duration, or quality.</li>
          <li>Add music, graphics, intros, outros, subtitles, or captions to the content.</li>
          <li>Translate or subtitle the content into other languages.</li>
          <li>Create promotional clips, trailers, or highlight reels from the content.</li>
        </ul>
        <p>All editing will be performed in a manner that does not materially misrepresent your views or statements. If you have any concerns about how your content has been edited, please contact us before publication is finalised.</p>
      </>
    ),
  },
  {
    id: "publishing-rights",
    title: "5. Publishing and Distribution Rights",
    content: (
      <>
        <p>You grant {BRAND} a perpetual, royalty-free, worldwide licence to publish, distribute, reproduce, and promote the podcast content across its official channels, including but not limited to:</p>
        <ul>
          <li>The {BRAND} official website (<strong>manthanfoundation.in</strong>).</li>
          <li>YouTube and video streaming platforms.</li>
          <li>Facebook, Instagram, LinkedIn, and other social media platforms.</li>
          <li>Email newsletters and digital communications.</li>
          <li>Print or digital promotional materials, brochures, or reports.</li>
          <li>Educational presentations, events, or exhibitions organised by {BRAND}.</li>
        </ul>
        <p>You also grant {BRAND} the right to republish or repurpose previously released content at any time.</p>
      </>
    ),
  },
  {
    id: "media-usage",
    title: "6. Media Usage Consent",
    content: (
      <>
        <div className="highlight-box">
          <p><strong>By participating in this podcast, you explicitly confirm that:</strong></p>
        </div>
        <p>You grant <strong>{BRAND}</strong> full permission to record, edit, reproduce, publish, distribute, exhibit, and promote any audio, video, or photographic content capturing your likeness, voice, image, and contributions.</p>
        <p>The content may be published on all official platforms of {BRAND} including the Website, YouTube, Facebook, Instagram, LinkedIn, and any other official digital or print promotional channels, both current and future.</p>
        <p>You understand that reasonable editing may be performed for quality, duration, format, or promotional purposes, without materially altering the overall meaning of your content.</p>
        <p>This permission is given voluntarily, without expectation of financial compensation, and applies for the duration that the content remains in use by {BRAND}.</p>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "7. Intellectual Property",
    content: (
      <>
        <p>All podcast content, recordings, and derivative materials produced by {BRAND} are the exclusive intellectual property of {BRAND}.</p>
        <p>Where you share original educational materials, presentations, or resources during the podcast:</p>
        <ul>
          <li>You retain ownership of your original materials.</li>
          <li>You grant {BRAND} a non-exclusive licence to use those materials in connection with the podcast.</li>
          <li>You confirm that the materials you share are your own and do not infringe upon any third party's intellectual property rights.</li>
        </ul>
        <p>You may share, link to, or embed the published podcast content on your personal or professional platforms, provided you attribute {BRAND} as the producer.</p>
      </>
    ),
  },
  {
    id: "consent-withdrawal",
    title: "8. Consent Withdrawal",
    content: (
      <>
        <p>You may withdraw your consent to participate at any time prior to the recording session by notifying us in writing at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>
        <p>After a recording session has taken place but before publication, withdrawal requests will be considered on a case-by-case basis.</p>
        <p>Once content has been published on third-party platforms (such as YouTube or social media), {BRAND} cannot guarantee removal from those platforms. We will make reasonable efforts to remove content from platforms we directly control upon receipt of a valid withdrawal request.</p>
        <p>Withdrawal of consent will not affect any prior lawful use of your content.</p>
      </>
    ),
  },
  {
    id: "responsibilities",
    title: "9. Participant Responsibilities",
    content: (
      <>
        <p>As a podcast participant, you agree to:</p>
        <ul>
          <li>Attend your confirmed recording session at the agreed date, time, and format (online or in-person).</li>
          <li>Provide at least 24 hours' notice if you are unable to attend and need to reschedule.</li>
          <li>Ensure a quiet, professional environment for recording (for online sessions).</li>
          <li>Dress and present yourself professionally, consistent with your role as an educator.</li>
          <li>Prepare your topic and any supporting materials in advance.</li>
          <li>Speak truthfully and avoid sharing false, defamatory, or misleading information.</li>
          <li>Avoid disclosing confidential information about students, colleagues, or your institution.</li>
        </ul>
      </>
    ),
  },
  {
    id: "code-of-conduct",
    title: "10. Code of Conduct",
    content: (
      <>
        <p>All participants are expected to maintain the highest standards of professional conduct throughout the application and participation process. The following conduct is not permitted:</p>
        <ul>
          <li>Discrimination, harassment, or disrespectful behaviour towards {BRAND} staff, other participants, or any group.</li>
          <li>Sharing content that is politically divisive, communally sensitive, or legally problematic.</li>
          <li>Promoting specific religious, political, or commercial agendas during the podcast.</li>
          <li>Making claims about professional achievements that cannot be substantiated.</li>
          <li>Sharing confidential or proprietary information without authorization.</li>
        </ul>
        <p>{BRAND} reserves the right to terminate a recording session and withdraw a participant from the programme if this Code of Conduct is violated.</p>
      </>
    ),
  },
  {
    id: "skilled-teachers-disclaimer",
    title: "11. Skilled Teachers Initiative — Disclaimer",
    content: (
      <>
        <div className="highlight-box">
          <p><strong>Important — Please Read Carefully</strong></p>
        </div>
        <p>Participation in the Skilled Teachers initiative, podcast programme, training activities, or any related {BRAND} programme <strong>does not</strong> guarantee, imply, or constitute:</p>
        <ul>
          <li>Employment, appointment, or placement with {BRAND} or any associated organisation.</li>
          <li>Selection for any leadership, promotional, or recognition programme.</li>
          <li>Academic certification beyond the stated programme certificate.</li>
          <li>Future podcast participation, collaboration, or engagement with {BRAND}.</li>
          <li>Any financial compensation, allowances, or benefits.</li>
        </ul>
        <p>Any future selection for interviews, recognition, collaboration, employment, or promotional opportunities will be based solely on independent evaluation, merit assessment, and the prevailing organisational requirements of {BRAND} at that time.</p>
        <p>{BRAND} makes no representation or warranty, express or implied, regarding any future professional benefit arising from participation.</p>
      </>
    ),
  },
  {
    id: "no-compensation",
    title: "12. No Compensation",
    content: (
      <>
        <p>Participation in the {BRAND} podcast is entirely voluntary and non-commercial. Unless explicitly stated in a separate written agreement:</p>
        <ul>
          <li>Participants will not receive any financial compensation, honorarium, royalties, or monetary benefit for their participation.</li>
          <li>Participants will not receive any goods, gifts, or non-monetary benefits in exchange for their participation.</li>
          <li>Travel, accommodation, or other expenses, if applicable, will only be reimbursed where specifically agreed in advance and in writing.</li>
        </ul>
      </>
    ),
  },
  {
    id: "limitation-liability",
    title: "13. Limitation of Liability",
    content: (
      <>
        <p>To the maximum extent permitted by applicable law, {BRAND} shall not be liable to any participant for:</p>
        <ul>
          <li>Any loss of professional reputation arising from published podcast content.</li>
          <li>Any technical failure during recording sessions.</li>
          <li>Any loss of data, recordings, or submitted materials due to unforeseen technical issues.</li>
          <li>Any indirect, incidental, or consequential damages arising from participation.</li>
        </ul>
        <p>Participants agree that they participate entirely at their own discretion and accept full responsibility for ensuring that their participation is appropriate within their professional context.</p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "14. Governing Law",
    content: (
      <>
        <p>These Podcast Terms shall be governed by and construed in accordance with the laws of India. Any dispute arising from participation in the podcast shall be subject to the exclusive jurisdiction of the courts of India.</p>
      </>
    ),
  },
  {
    id: "contact",
    title: "15. Contact",
    content: (
      <>
        <p>If you have any questions about these Podcast Participation Terms or wish to withdraw your consent, please contact us:</p>
        <div className="highlight-box">
          <p><strong>{BRAND} — Podcast Team</strong></p>
          <p>Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
          <p>Website: <Link to="/contact">Contact Page</Link></p>
        </div>
      </>
    ),
  },
];

export default function PodcastTerms() {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    api.get("/cms/legal").then((r) => {
      if (r.data?.podcast_terms_custom) setCustomContent(r.data.podcast_terms_custom);
    }).catch(() => {});
  }, []);

  if (customContent) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-[#0F172A] text-white">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">Podcast Participation Terms</h1>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-gray-600 text-[15px] leading-relaxed legal-content"
          dangerouslySetInnerHTML={{ __html: customContent }} />
        <style>{`.legal-content p{margin-bottom:.75rem}.legal-content ul{list-style:disc;padding-left:1.5rem;margin:.75rem 0}.legal-content h2{font-size:1.15rem;font-weight:600;color:#1e293b;margin:1.5rem 0 .5rem}`}</style>
      </div>
    );
  }

  return (
    <LegalPage
      title="Podcast Participation Terms & Consent"
      subtitle="By participating in the Manthan Foundation Skilled Teachers Podcast, you agree to the following terms. Please read them carefully before submitting your application."
      lastUpdated="1 July 2025"
      breadcrumb="Podcast Terms"
      sections={DEFAULT_SECTIONS}
    />
  );
}
